import axios, { AxiosInstance } from 'axios'
import configService from '@/services/configService'
import { get_jwt_token } from '@/services/auth'
import { ApplicationConfiguration } from '@/types'
import router from '@/router' // Import the router to handle redirections
import { useUser } from '@/composables/useUser'


let api: AxiosInstance | null = null

export enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
  OPTIONS = 'OPTIONS',
  HEAD = 'HEAD',
}

const initializeApi = async (): Promise<AxiosInstance> => {
  if (!api) {
    await configService.loadApplicationConfig()
    const appConfig = configService.getApplicationConfigData() as ApplicationConfiguration

    if (!appConfig || !appConfig.api?.url) {
      throw new Error('API URL is not defined in the configuration')
    }

    const APLOS_DOMAIN = appConfig.api.url

    api = axios.create({ baseURL: APLOS_DOMAIN })

    api.interceptors.request.use(async (requestConfig) => {
      requestConfig.headers = requestConfig.headers ?? {}
      const jwt = await get_jwt_token()
      requestConfig.headers.Authorization = `Bearer ${jwt}`
      return requestConfig
    })

    // Response Interceptor: Handle 401 errors (not authenticated)
    api.interceptors.response.use(
      (response) => response, // Pass through if the response is successful
      (error) => {
        if (error.response && error.response.status === 401) {
          router.replace('/') // Redirect to login page if a 401 error occurs
        }
        return Promise.reject(error) // Reject the error to handle it elsewhere if needed
      }
    )
  }

  return api // Ensure AxiosInstance is always returned
}

export function getTenantPath(tenantId?: string) {
  if (!tenantId) {
    throw new Error("tenant id is required.")
  }

  return `/tenants/${tenantId}`
}

export function getUserPath(tenantId?: string, userId?: string) {
  
  const basePath = `${getTenantPath(tenantId)}/users`;
  return userId ? `${basePath}/${userId}` : basePath;
    
}

export const submitApiRequest = async (method: string, url: string, data?: any) => {
  const api = await initializeApi()
  const requestConfig = data ? { method, url, data } : { method, url }
  return api.request(requestConfig)
}

const aplosApi = {
  apiInstance: new Proxy<AxiosInstance>({} as AxiosInstance, {
    get: function (_, prop) {
      if (prop === 'then') {
        return api
      }
      return async (...args: any[]) => {
        const instance = await initializeApi()
        const method = (instance as any)[prop]
        if (typeof method === 'function') {
          return method.apply(instance, args)
        }
        return method
      }
    }
  }),

  // API methods
  getFiles: async (tenantId?: string, userId?: string, status?: string) => {

    const dt = new Date(Date.now())
    const time = dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, second: '2-digit' });
    console.debug(`getting files: ${time}`);
    // only add the querystring if status is not undefined
    let querystring = ""
    if (status) {
      querystring = ("?status=" + status)
    }
    return await submitApiRequest('get', `${getUserPath(tenantId, userId)}/nca/files` + querystring)
  },

  executeAnalysis: async (
    tenantId?: string,
    userId?: string,
    fileId?: string,
    configs_json?: string,
    meta_data_json?: string,
    data_processing?: string | Record<string, any>,
    post_processing?: string | Record<string, any>
  ) => {

    

    if (!fileId) throw new Error('File ID is required')
    if (!configs_json) throw new Error('Configs JSON is required')
    
    let data_p : any = {}
    if (data_processing) {
      // check if it's a string
      if (typeof data_processing === 'string') {            
        data_p = JSON.parse(data_processing)
      }
      if (typeof data_processing === 'object'){
        data_p = data_processing
      
      }
    
    }

    let post_p : any = {}
    if (post_processing) {
      // check if it's a string
      if (typeof post_processing === 'string') {            
        post_p = JSON.parse(post_processing)
      }
      if (typeof post_processing === 'object'){
        post_p = post_processing
      
      }
    
    }

    const payload = {
      file: { id: fileId },
      configuration: configs_json,
      meta_data: meta_data_json,
      data_processing: {},
      post_processing: {}
      
    }
    if (data_p) {
      if (data_p.data_processing) {        
        payload.data_processing =data_p.data_processing;
      }
      else if (data_p) {        
        payload.data_processing =data_p;
      }
    }

    if (post_p) {
      if (post_p.post_processing) {        
        payload.post_processing =post_p.post_processing;
      } else if (post_p) {
        payload.post_processing =post_p;
      }
    }
    
    const results = await submitApiRequest(
      'post',
      `${getUserPath(tenantId, userId)}/nca/executions`,
      payload
    )
    return results
  },

  getExecution: async (tenantId?: string, userId?: string, executionId?: string) => {
    return await aplosApi.getExecutionStatus(tenantId, userId, executionId)
  },

  getExecutionStatus: async (tenantId?: string, userId?: string, executionId?: string) => {
    return await submitApiRequest(
      'get',
      `${getUserPath(tenantId, userId)}/nca/executions/${executionId}`
    )
  },

  getExecutionPreProcessRules: async (tenantId?: string, userId?: string, executionId?: string) => {
    return await submitApiRequest(
      'get',
      `${getUserPath(tenantId, userId)}/nca/executions/${executionId}/data-processing`
    )
  },

  getExecutionPostProcessRules: async (tenantId?: string, userId?: string, executionId?: string) => {
    return await submitApiRequest(
      'get',
      `${getUserPath(tenantId, userId)}/nca/executions/${executionId}/post-processing`
    )
  },

  getFileData: async (tenantId?: string, userId?: string, fileId?: string) => {
    return await submitApiRequest(
      'get',
      `${getUserPath(tenantId, userId)}/nca/files/${fileId}/data`
    )
  },

  getFileHeaders: async (tenantId?: string, userId?: string, fileId?: string) => {
    return await submitApiRequest(
      'get',
      `${getUserPath(tenantId, userId)}/nca/files/${fileId}`
    )
  },

  getExecutions: async (tenantId?: string, userId?: string, limit?: number, filters: string | null= null, lastKey?: string, start_date?: string, end_date?: string) => {
    const auth = useUser();
    await auth.initialize();
    limit = limit || auth.userSettings.execution_response_limit
    filters = filters ? `?${filters}` : `?limit=${limit}`;
    if (lastKey) {
      const b64 = btoa(JSON.stringify(lastKey));
      filters += `&last-key=${b64}`;
    }
    if (start_date) {
      filters += `&start-date=${start_date}`;
    }
    if (end_date) {
      filters += `&end-date=${end_date}`;
    }
    const path = `${getUserPath(tenantId, userId)}/nca/executions${filters}`;
    
    const results = await submitApiRequest('get', path)
    return results
  },

  getFileUploadUrl: async (tenantId?: string, userId?: string, data?: any) => {
    return await submitApiRequest('post', `${getUserPath(tenantId, userId)}/nca/files`, data)
  },

  getUser: async (tenantId?: string, userId?: string) => {
    return await submitApiRequest('get', `${getUserPath(tenantId, userId)}`)
  },

  executeValidations: async (tenantId?: string, userId?: string, data?: any) => {
    return await submitApiRequest('post', `${getUserPath(tenantId, userId)}/nca/validations`, data)
  },

  getUserMetrics: async (tenantId?: string, userId?: string) => {
    return await submitApiRequest('get', `${getUserPath(tenantId, userId)}/metrics`)
  },

  updateUser: async (tenantId?: string, userId?: string, data?: any) => {
    return await submitApiRequest('post', `${getUserPath(tenantId, userId)}`, data)
  },
  getValidations: async (tenantId?: string, userId?: string) => {
    return await submitApiRequest('get', `${getUserPath(tenantId, userId)}/nca/validations`)
  },
  downloadValidationReport: async (tenantId?: string, userId?: string, batchId?: string) => {
    return await submitApiRequest(
      'get',
      `${getUserPath(tenantId, userId)}/nca/validations/${batchId}/report`
    )
  },
  downloadValidationFiles: async (tenantId?: string, userId?: string, batchId?: string) => {
    return await submitApiRequest(
      'get',
      `${getUserPath(tenantId, userId)}/nca/validations/${batchId}/files`
    )
  },
  initiatingValidations: async (tenantId?: string, userId?: string, data?: any) => {
    const result = await submitApiRequest(
      'post',
      `${getUserPath(tenantId, userId)}/nca/validations`,
      data
    )
    return result
  },
  getTenants: async (tenantId?: string, filter?: string) => {
    return await submitApiRequest('get', tenantId ? `tenants/${tenantId}` : `tenants?status=${filter}`)
  },
  getTenant: async (tenantId?: string, filter?: string) => {
    return await submitApiRequest('get', tenantId ? `tenants/${tenantId}` : `tenants?status=${filter}`)
  },
  getTenantUsers: async (tenantId?: string) => {
    return await submitApiRequest('get', `${getTenantPath(tenantId)}/users`)
  },
  getTenantsSubscriptionList: async (tenantId?: string) => {
    return await submitApiRequest('get', `${getTenantPath(tenantId)}/subscriptions/`)
  },
  updateTenant: async (data?: any, tenantId?: any) => {
    const result = await submitApiRequest('post', `${getTenantPath(tenantId)}`, data);
    return result;
  },

  createTenant: async (data?: any) => {
    const result = await submitApiRequest('post', `tenants/`, data);
    return result;
  },

  updateSubscription: async (tenantId?: string, data?: any) => {
    return await submitApiRequest('post', `${getTenantPath(tenantId)}/subscriptions/${data?.subscription?.id ?? ""}`, data)
  },
  getAssignableRoles: async (tenantId?: string, userId?: string) => {
    const result = await submitApiRequest('get', `${getUserPath(tenantId, userId)}/assignable-roles`,);
    return result;
  },
  getPublicOutMessages: async () => {
    return await submitApiRequest('get', `/app/messages`)
  },
  getUserMessages: async (tenantId?: string, userId?: string) => {
    return await submitApiRequest('get', `${getUserPath(tenantId, userId)}/messages`)
  },

  getTenantMetrics: async (tenantId?: string) => {
    return await submitApiRequest('get', `${getTenantPath(tenantId)}/metrics`)
  },

  getCompanyLogo: async (tenantId: string, userId: string, fileId: string) => {
    return await submitApiRequest('get', `${getUserPath(tenantId, userId)}/company/logos/${fileId}`)
  },

  saveCompanyLogo: async (tenantId: string, userId: string, data?: any) => {
    return await submitApiRequest('post', `${getUserPath(tenantId, userId)}/company/logos/`, data)
  },

  deleteCompanyLogo: async (tenantId: string, userId: string, logoId: string) => {
    return await submitApiRequest('delete', `${getUserPath(tenantId, userId)}/company/logos/${logoId}`)
  },

  getDownloadFilePresignedUrl: async (tenantId?: string, userId?: string, fileId?: string) => {
    return await submitApiRequest(
      'get',
      `${getUserPath(tenantId, userId)}/nca/files/${fileId}/download-url`
    )
  },
  deleteFile: async (tenantId?: string, userId?: string, fileId?: string) => {
    return await submitApiRequest('post', `${getUserPath(tenantId, userId)}/nca/files/${fileId}/delete`)
  },
  archiveFile: async (tenantId?: string, userId?: string, fileId?: string) => {
    return await submitApiRequest('post', `${getUserPath(tenantId, userId)}/nca/files/${fileId}/archive`)
  },

  unarchiveFile: async (tenantId?: string, userId?: string, fileId?: string) => {
    return await submitApiRequest('post', `${getUserPath(tenantId, userId)}/nca/files/${fileId}/unarchive`)
  },

};

export default aplosApi
